import React from 'react'
import { cn } from '@s4l/utility-css'
import debug from 'debug'

import Typography from '../Typography'

import styles from './index.module.css'

const log = debug('s4l:ds:Rating')

interface IRatingProps {
  size: 'small' | 'large' | 'lite'
  value: number
  comments?: number
  i18n?: {
    quality: {
      poor: string
      fair: string
      good: string
      veryGood: string
      excellent: string
    }
    basedOn: string
    reviews: string
  }
}

const getQualityFromValue = (value: number) => {
  if (value <= 5) return 'poor'
  if (value <= 6.5) return 'fair'
  if (value <= 8) return 'good'
  if (value <= 8.5) return 'veryGood'

  return 'excellent'
}

export default function Rating(props: Readonly<IRatingProps>) {
  const { value, size, comments, i18n } = props

  const isLarge = size === 'large'

  if (value < 0 || value > 10) {
    log('Rating must be between 0 and 10')
    return null
  }

  if (!i18n && size !== 'lite') {
    log('i18n is required for small and large sizes')
    return null
  }
  if (!comments) return null

  const quality = getQualityFromValue(value)
  const qualityText = i18n?.quality[quality] ?? ''

  const qualityPropsLarge = {
    as: 'h6',
    variant: 'title',
    size: 'xs',
    bold: true,
  } as const

  const qualityPropsSmall = {
    as: 'p',
    variant: 'caption',
    size: 'l',
    bold: true,
  } as const

  const qualityProps = isLarge ? qualityPropsLarge : qualityPropsSmall

  return (
    <div
      className={cn(styles.container, {
        [styles.poor]: value <= 5,
        [styles.fair]: value > 5 && value <= 6.5,
        [styles.good]: value > 6.5,
      })}
      data-size={size}
    >
      <div data-rating="true" className={cn(styles.rating, { [styles.small]: !isLarge })}>
        <Typography {...qualityProps}>{value.toFixed(1)}</Typography>
      </div>
      {size !== 'lite' && (
        <div className={styles.qualityAndComments}>
          <div data-quality-text="true">
            <Typography {...qualityProps}>{qualityText}</Typography>
          </div>
          <div>
            {isLarge && (
              <Typography variant="body" size="l">
                {i18n?.basedOn}
              </Typography>
            )}
            <span className={styles.commentsCount}>
              <Typography
                bold={isLarge}
                variant={isLarge ? 'body' : 'caption'}
                size={isLarge ? 'l' : 'm'}
              >
                {comments} {i18n?.reviews}
              </Typography>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
